import type { KeyboardEvent, ReactNode, Ref } from "react";

import "./viewer.css";

export type ViewerState = "loading" | "ready" | "failed" | "nowebgl";
export type ViewerToggle = "wireframe" | "size" | "figure";

/** The only sentences the 3D view ever says ([ux §8.4]). They are text in the
 *  page, never drawn in the canvas, and never an id, a URL or an error text. */
export const VIEWER_COPY = {
  loading: "Loading 3D view…",
  failed: "The 3D view could not load. The object itself is fine.",
  nowebgl: "Your browser cannot show 3D here.",
  quick: "Quick 3D view. Rendered ads look richer.",
  simplified: "Simplified view.",
} as const;

const TOGGLES: [ViewerToggle, string][] = [
  ["wireframe", "Wireframe"],
  ["size", "Size"],
  ["figure", "Person for scale"],
];

/** The DOM around the 3D canvas: toolbar, the still picture behind, the
 *  sentences. It imports nothing from three, so it is tested in jsdom and
 *  shared by the real viewer and the test stub.
 *
 *  `toggles` null leaves only “Reset view” (a surface, an artwork);
 *  `toggles.figure` null leaves out “Person for scale” (an object under
 *  30 cm). */
export function ViewerFrame(props: {
  name: string;
  state: ViewerState;
  thumbUrl: string | null;
  caption: string | null;
  toggles: { wireframe: boolean; size: boolean; figure: boolean | null } | null;
  onToggle(key: ViewerToggle): void;
  onReset(): void;
  /** Shown while “Size” is on. */
  sizeText: string | null;
  dimmed?: boolean;
  /** Drawn over the canvas: the design studio's “Rebuilding…”. */
  overlay?: ReactNode;
  canvasRef?: Ref<HTMLCanvasElement>;
  onKeyDown?(e: KeyboardEvent<HTMLCanvasElement>): void;
}) {
  const { state, toggles } = props;
  const drawing = state === "loading" || state === "ready";
  return (
    <div className="viewer" data-state={state}>
      {drawing && (
        <div role="toolbar" aria-label="3D view" className="viewer-toolbar">
          {toggles &&
            TOGGLES.map(([key, label]) => {
              const pressed = toggles[key];
              if (pressed === null) return null;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={pressed}
                  disabled={state !== "ready"}
                  onClick={() => props.onToggle(key)}
                >
                  {label}
                </button>
              );
            })}
          <button
            type="button"
            disabled={state !== "ready"}
            onClick={props.onReset}
          >
            Reset view
          </button>
        </div>
      )}
      <div className="viewer-box">
        {state !== "ready" && props.thumbUrl && (
          <img className="viewer-thumb" src={props.thumbUrl} alt="" />
        )}
        <canvas
          ref={props.canvasRef}
          role="img"
          tabIndex={0}
          aria-label={`3D view of ${props.name}. Drag to turn, pinch or scroll to zoom.`}
          className={props.dimmed ? "viewer-dimmed" : undefined}
          style={{ touchAction: "none" }}
          hidden={!drawing}
          onKeyDown={props.onKeyDown}
        />
        {state === "ready" && props.sizeText && (
          <p className="viewer-size">{props.sizeText}</p>
        )}
        {state === "loading" && (
          <p role="status" className="viewer-status">
            {VIEWER_COPY.loading}
          </p>
        )}
        {state === "failed" && (
          <p role="alert" className="viewer-status">
            {VIEWER_COPY.failed}
          </p>
        )}
        {state === "nowebgl" && (
          <p role="status" className="viewer-status">
            {VIEWER_COPY.nowebgl}
          </p>
        )}
        {props.overlay && <div className="viewer-overlay">{props.overlay}</div>}
      </div>
      {drawing && props.caption && (
        <p className="viewer-caption">{props.caption}</p>
      )}
    </div>
  );
}
